/**
 * ThreadDetailPane — the LEFT half of a thread's workspace (Phase 2, kanban
 * 12584b08): what Merlin found and what Jess decides.
 *
 * Top to bottom: the thread's identity and posture, the decision bar (open a
 * case / done / send to someone / not an issue — or reopen / open a case on a
 * closed thread) with its inline forms, the INV case file when the thread is
 * a case (the ONE canonical case view, with ⤢ to pop it out), the asks that
 * belong here, Merlin's filed brief (the problem, how we got here as a
 * chain of causes, key facts, do next, what is still unconfirmed; the trace and the rest fold away by the timeline), and the evidence —
 * timeline, join keys, the signals themselves with NEW markers for anything
 * that landed since the reader last looked, formal complaints.
 *
 * Ported from the retired full-viewport ThreadTriageBoard: same actions,
 * same copy, same audit semantics; the conversation now lives in the pane
 * beside this one instead of underneath a 44vh case box.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { AssistantMarkdown } from '../../components/agentConsole/AssistantTurnView';
import AudioNotesInput from '../../components/portal/AudioNotesInput';
import { CaseFileModal } from '../../components/portal/CaseFileModal';
import { useCaseFilePopout } from '../../components/portal/caseFileExpand';
import {
  actorWord,
  externalClosureNoticeForIssues,
  signalCountLabel,
  SOURCE_TAG_LABEL,
  THREAD_SEVERITY_LABEL,
  failedActionMessage,
} from '../../components/portal/investigationsView';
import {
  WORK_STATE_LABEL,
  WORK_STATE_TONE,
  ageLabel,
  evidenceBridgeKeys,
  groupSignalsByDay,
  hasPostClosureNote,
  investigatedAtLabel,
  isReopened,
  organizeChips,
  postClosureLine,
  reopenedLine,
  severityEdge,
  signalDateLabel,
  splitChildren,
  splitParentNo,
  threadByNo,
  isRoutedToUser,
  workStateLabel,
} from '../../components/portal/threadTriageView';
import { PendingFollowupsLine } from './PendingFollowupsLine';
import type { MerlinFollowupRead } from '../../services/merlinFollowups';
import { splitAsks } from '../../components/quality/asksView';
import { AskModal } from '../../components/quality/AskModal';
import { AskStatusCard } from '../../components/quality/AskStatusCard';
import {
  completeThread,
  addThreadNote,
  dismissThread,
  getThreadHistory,
  listThreadNotes,
  listThreadRouteTargets,
  listThreadSignals,
  openThreadInvestigation,
  reopenThread,
  handOffInvestigation,
  rerouteThread,
  sendHandoffAnyway,
  setThreadWorkState,
  setThreadWait,
  clearThreadWait,
  acknowledgeThreadWait,
  type DoorInvestigationRead,
  type DoorRouteDeliveryRead,
  type DoorThreadRouteTargetsRead,
  type RouteRole,
  type DoorThreadRead,
  type DoorThreadSignalsResponse,
  type DoorCaseTaskRead,
  type ThreadWorkState,
} from '../../services/qcpInvestigations';
import { trackEvent } from '../../services/portalTracking';
import { discardDataRequest, type DoorDataRequestRead } from '../../services/qcpDataRequests';
import { DismissControl } from '../../components/quality/DismissControl';
import { keyLabel } from '../../components/portal/keyLabel';
import { TASK_OWNER_LABEL } from '../../components/portal/taskOwner';
import { CUSTOMER_STEP_HINT, defaultCustomerRecommendation } from '../../components/portal/customerStep';
import { useCustomerRelay } from '../../components/quality/useCustomerRelay';
import { CauseChain } from './CauseChain';
import { CaseAsksHost, CaseFileSection } from './CaseFileSection';
import { briefPlan, taskDesksFor } from './briefPlan';
import { deliveryOutcome, gatedHandoffLine, gatedHandoffs, joinNames, neverEmailed } from './deliveryView';
import { assignedByShort, whereFromLine } from './routeOriginView';
import { checkTypedNames, pickFor, resolvedNames, type NameCheck } from './routeNameCheck';
import { foldedLabel, quietTimeline, sentBy } from './timelineView';
import { caseActivity, lastActivity, sinceLastLook } from './sinceLastLook';
import { SinceLastLookCard } from './SinceLastLookCard';
import { FoldLines } from './FoldLines';
import { IssueSummaryCard } from './IssueSummaryCard';
import {
  daysOverdue,
  waitingOn,
  waitingOnLabel,
  waitingOverdue,
} from './waitingOnView';
import { daysWord, postureWords } from './stateWords';
import {
  deskAskGroups,
  deskDrafts,
  deskOwnedDraftIds,
  pillLabel,
  briefDetailParts,
  chainStepsDriving,
  stepEvidenceFor,
  briefKeyFacts,
  causeChainSteps,
  doNextByDesk,
  factsOffTheChain,
  keyFactItems,
  stepsForDesks,
} from './merlinBrief';
import type { DeskAskGroup, DeskGroup } from './merlinBrief';
import { SituationTiles } from './SituationTiles';
import { DoNextWhy } from './DoNextWhy';
import { EvidenceRefs } from './EvidenceRefs';
import { isNewSignal, newSignalCount } from './qualityWorkspaceView';
import { handOffReply, respondedWhen, responseToWait } from './respondedView';
import { hasWhyDetails, ruleStateLabel, rulesOnFileLine, whyLine, whyRules } from '../../components/portal/routingRationaleView';
import { NoteBox } from '../../components/portal/NoteBox';
import { returnFocusToList } from './doorFocus';
import { useHistoryPages } from '../../components/portal/useHistoryPages';
import styles from './QualityWorkspace.module.css';
import { composeRouteTo } from './routeTargetsView';
import { flaggedChecks, ROUTE_SENDING_LINE, routePick, sendLabel } from './routePickView';
import { RouteRoleChoices } from './RouteRoleChoices';
import { RoutePeoplePicks } from './RoutePeoplePicks';
import { RouteConfirmLine } from './RouteConfirmLine';
import { primaryRef } from './caseIdentity';
import { AlsoKnownAsLine } from './AlsoKnownAsLine';
import pickerStyles from './RoutePicker.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

const MERLIN_ICON = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3l1.9 4.6 4.6 1.9-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
  </svg>
);

/** Where "Details ▸" on the issue summary takes the reader. */
const MERLIN_READ_ID = 'merlin-read';

/** A routing reason longer than this is held to one line until opened. */
const WHY_FOLD_AT = 90;

/**
 * What each decision does, in a tooltip on its button and under "What each
 * does ▸" (Scott, 2026-10-06: the always-on captions ate the pane).
 */
const DECISION_HINTS = {
  open: 'Becomes a case on Cases. Yours to track to closed.',
  done: 'Closes it to Done as a real issue. No one is emailed.',
  route: 'Emails them. Theirs to decide; you see it under Routed away.',
  dismiss: 'Closes it to Done. Merlin learns to skip ones like it.',
} as const;

/**
 * Which inline step is open under the decision bar. The `confirm*` modes are
 * the one-line "are you sure" for the controls that change the item in one
 * press (fan-out finding 1: two careful readers opened a case and closed an
 * item by accident, expecting a form like "Not an issue" has).
 */
type ActionMode =
  | 'none'
  | 'reroute'
  | 'handoff'
  | 'dismiss'
  | 'posture'
  | 'reopen'
  | 'confirmOpen'
  | 'confirmDone'
  | 'confirmPullBack';

export interface ThreadDetailPaneProps {
  thread: DoorThreadRead;
  /** Every tracked thread — for split-lineage navigation. */
  threads: DoorThreadRead[];
  /** The INV case behind this thread, once the case list has caught up. */
  caseFile: DoorInvestigationRead | null;
  /**
   * The seen-mark captured BEFORE this visit marked the thread read — the
   * NEW markers compare signals against it. Null = never read here.
   */
  seenBefore: string | null;
  /**
   * When this viewer last opened the item on ANY browser, as the server
   * recorded it (card 10a54f95): the "Since you last looked" line diffs the
   * timeline and the asks against it. Null = first look (no line);
   * undefined = not known yet (no line either).
   */
  lastSeenAt?: string | null;
  /** The asks that belong on this thread / its case. */
  asks: DoorDataRequestRead[];
  onAskUpdated: (updated: DoorDataRequestRead) => void;
  onThreadUpdated: (updated: DoorThreadRead) => void;
  onInvestigationOpened: (
    updated: DoorThreadRead,
    investigation: DoorInvestigationRead,
  ) => void;
  onInvestigationUpdated: (updated: DoorInvestigationRead) => void;
  /** A split-lineage chip was clicked — the page navigates there. */
  onJumpToThread: (thread: DoorThreadRead) => void;
  /**
   * The signed-in user's name. A live item routed to someone else is theirs
   * to decide: it shows who has it and Pull back, not the four decision
   * buttons (turn 1: they stayed active after the explorer sent an item on).
   * Null keeps the decision bar (no user to compare against).
   */
  viewerName?: string | null;
  /** An ask went out from here: refetch the door's asks, so drafts the
   *  send approved stop reading "not sent" (proposal 2). */
  onAskSent?: () => void;
  /** The follow-ups Merlin promised on this item (kanban 43160d8b): the
   *  open ones read "Merlin will check back on …" under the banners. */
  followups?: MerlinFollowupRead[];
  /**
   * "Ask <desk>" on one of the case file's outstanding items (also from
   * the pop-out): the page owns the one task-ask modal for the workspace,
   * so the asks rail and the case file open the same one (kanban 445962e2).
   */
  onAskTask: (task: DoorCaseTaskRead) => void;
}

/**
 * Once a case is open, its file (above) is the record: checklist, findings,
 * asks. Merlin's original write-up repeated the same next steps a second
 * time, so it folds away — still one click off (iteration 6; Jess: the
 * traced narrative "means nothing to me" unless he goes looking).
 */
function FoldOnceCaseExists({ folded, children }: { folded: boolean; children: ReactNode }) {
  if (!folded) return <>{children}</>;
  return (
    <details className={styles.briefDetails} data-testid="merlin-read-folded">
      <summary>Merlin’s first write-up, from before the case was opened</summary>
      <div className={styles.briefDetailsBody}>{children}</div>
    </details>
  );
}

/**
 * The one-step "are you sure" under the decision bar: what will happen in
 * plain words, the action, and a way out. Same frame as the inline forms.
 */
function ConfirmStep({
  label,
  question,
  detail,
  yes,
  yesClass,
  no,
  busy,
  onYes,
  onNo,
  track,
}: {
  label: string;
  question: string;
  detail: string;
  yes: string;
  yesClass: string;
  no: string;
  busy: boolean;
  onYes: () => void;
  onNo: () => void;
  /** Recorder family for the yes press, keyed by the thread; the button's
   *  words ("Close it as handled") are not a step name (kanban 529c0851). */
  track: { yes: string; threadNo: string };
}) {
  return (
    <div
      className={styles.inlineAction}
      role="group"
      aria-label={label}
      data-testid="confirm-step"
      data-target-thread-id={track.threadNo}
    >
      <div className={styles.inlineActionHead}>
        {question}
        <span className={styles.inlineActionSub}>{detail}</span>
      </div>
      <div className={styles.inlineActionBtns}>
        <button
          type="button"
          className={yesClass}
          onClick={onYes}
          disabled={busy}
          data-testid="confirm-yes"
          data-action={track.yes}
        >
          {yes}
        </button>
        <button type="button" className={styles.actGhost} onClick={onNo} disabled={busy} data-action="qcp_decision_cancel">
          {no}
        </button>
      </div>
    </div>
  );
}

/**
 * What is already out with a desk, under its name in Do next (kanban
 * d3ec3001): one PILL per ask sent — who was asked, when, whether it was
 * answered and who was told — opening the status card (nudge, open the
 * thread, withdraw); and the "Ask <desk>" button that opens the modal with
 * Merlin's pre-written request (✉ when one is waiting). The button steps
 * aside while an ask is still waiting on this desk.
 */
function DeskAsks({
  desk,
  groups,
  drafts,
  onAsk,
  onOpen,
  onDraftsDiscarded,
  threadNo,
}: {
  desk: string;
  groups: DeskAskGroup[];
  /** Merlin's unsent drafts for this desk's steps (deskDrafts). */
  drafts: DoorDataRequestRead[];
  onAsk?: () => void;
  onOpen: (group: DeskAskGroup) => void;
  /** Fold each thrown-away draft into the door's list. */
  onDraftsDiscarded: (updated: DoorDataRequestRead) => void;
  threadNo: string;
}) {
  const readOnly = useQualityReadOnly();
  const waiting = groups.some((g) => g.state === 'waiting');
  const hasDraft = drafts.length > 0;
  if (groups.length === 0 && !onAsk) return null;
  // Don't ask (kanban 59a1ee04): every draft on this desk is thrown away
  // with the same note; the control records the press and shows a refusal.
  const discardDrafts = async (note: string | null) => {
    for (const d of drafts) onDraftsDiscarded(await discardDataRequest(d.id, 'discarded by Jess', note));
  };
  return (
    <div className={styles.deskAsks} data-testid="desk-asks">
      {groups.map((g) => (
        <button
          key={g.id}
          type="button"
          className={`${styles.deskAsk} ${styles.deskAskPill}`}
          data-state={g.state}
          title={`${g.title} (to ${g.recipients.map((r) => r.name).join(', ')})`}
          data-action="qcp_desk_ask_status_open"
          onClick={() => onOpen(g)}
        >
          <span className={styles.deskAskDot} aria-hidden="true" />
          {pillLabel(g)}
          {g.unseen && <span className={styles.deskAskNew}>new</span>}
        </button>
      ))}
      {onAsk && !readOnly && !waiting && (
        <button
          type="button"
          className={styles.deskAskBtn}
          data-ready={hasDraft || undefined}
          // "Review and send" on one of these drafts presses this button.
          data-draft-ids={hasDraft ? drafts.map((d) => d.id).join(' ') : undefined}
          onClick={onAsk}
          data-shortcut="a"
          data-shortcut-name="ask_colleague"
          // Named by the family, not "Ask Jess Finlaw, Kevin Platt": the
          // desk's people are the label, never the step (kanban 68b23ba8).
          data-action="qcp_ask_desk_open"
        >
          {hasDraft ? '✉ ' : ''}Ask {desk}
        </button>
      )}
      {onAsk && !readOnly && !waiting && hasDraft && groups.length === 0 && (
        <span className={styles.deskHint}>Merlin wrote the request when it filed — one click to send.</span>
      )}
      {onAsk && !readOnly && !waiting && hasDraft && (
        <DismissControl
          what={`Merlin's draft to ${desk}`}
          label="Don't ask"
          noteLabel="say why"
          placeholder="Why not? e.g. already answered in the orders review meeting"
          size="row"
          onDismiss={discardDrafts}
          track={{
            plain: 'qcp_ask_dismiss',
            note: 'qcp_ask_dismiss_note',
            // The form's own name: its accessible name is "say why: Merlin's
            // draft to <desk>", and a desk reads as its people (kanban 529c0851).
            form: 'qcp_draft_reason',
            targets: { thread_id: threadNo },
            context: { thread_no: threadNo, data_request_ids: drafts.map((d) => d.id), surface: 'do_next_desk', state: 'draft' },
          }}
        />
      )}
    </div>
  );
}

export function ThreadDetailPane({
  thread,
  threads,
  caseFile,
  seenBefore,
  lastSeenAt,
  asks,
  onAskUpdated,
  onThreadUpdated,
  onInvestigationOpened,
  onInvestigationUpdated,
  onJumpToThread,
  viewerName = null,
  onAskSent,
  onAskTask,
  followups = [],
}: ThreadDetailPaneProps) {
  const { popped: casePopped, open: popOutCase, close: closeCasePopout } =
    useCaseFilePopout();
  // A read-only viewer (kanban 808dabf6): the decision bars, forms, notes
  // and asks stay out of the DOM; what is shown is the same brief.
  const readOnly = useQualityReadOnly();
  const [actionMode, setActionMode] = useState<ActionMode>('none');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [routeTo, setRouteTo] = useState('');
  const [rerouteNote, setRerouteNote] = useState('');
  const [reopenNote, setReopenNote] = useState('');
  // What the last "Send it" actually did (emails out / nobody matched) —
  // rendered under the action bar so delivery is never silent.
  const [routeReceipt, setRouteReceipt] = useState<DoorRouteDeliveryRead | null>(null);
  // Door gate (kanban 8a4d2212): the hand-offs Merlin is still checking or
  // held as already known; Send anyway sends the brief now, as you.
  const gated = gatedHandoffs(thread);
  const sendAnyway = async (toEmail: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await sendHandoffAnyway(thread.id, toEmail);
      setRouteReceipt(result.delivery);
      onThreadUpdated(result.thread);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The hand-off could not be sent.');
    } finally {
      setBusy(false);
    }
  };
  // Who the picker can offer for THIS thread — the account's sales rep / CS
  // rep resolved from Merlin's data, plus the staff directory the backend
  // delivers against. Fetched when the reroute form first opens; reset per
  // thread (the roles depend on the thread's account).
  const [routeTargets, setRouteTargets] = useState<DoorThreadRouteTargetsRead | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<Set<RouteRole>>(() => new Set());
  // "Not mine because…" as a RULE (kanban 143454d3): the note / reason is
  // recorded as a standing routing instruction Merlin applies next time
  // and Scott can see and retire, not only a note on this item.
  const [standingRule, setStandingRule] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissRule, setDismissRule] = useState(false);
  const [postureNote, setPostureNote] = useState('');
  // "Waiting on <person>" in the where-it-stands form (case round, finding 4).
  const [waitWho, setWaitWho] = useState('');
  // The directory person picked for the wait (by email): two people can
  // share a name, and the record pins one of them.
  const [waitPickEmail, setWaitPickEmail] = useState<string | null>(null);
  const [waitUntil, setWaitUntil] = useState('');
  // Which folded runs of Merlin housekeeping are open in the timeline.
  const [openFolds, setOpenFolds] = useState<Record<string, boolean>>({});
  // The routing reason is held to one line until opened.
  const [whyOpen, setWhyOpen] = useState(false);
  // The decision captions, shown on request under the buttons.
  const [hintsOpen, setHintsOpen] = useState(false);
  const [filingChipsExpanded, setFilingChipsExpanded] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});
  // The INV just minted from THIS pane — headlines the case block so "Open
  // investigation" visibly lands somewhere instead of dead-ending.
  const [justOpenedInvId, setJustOpenedInvId] = useState<string | null>(null);
  // The Ask modal for one desk, and the status card behind a sent ask's pill
  // (kanban d3ec3001).
  const [askFor, setAskFor] = useState<DeskGroup | null>(null);
  const [statusFor, setStatusFor] = useState<DeskAskGroup | null>(null);
  // Evidence + timeline, fetched per thread. 'error' renders the honest
  // fallback (bare ids / no timeline) — the pane still works when the bank
  // is unreachable.
  const [signals, setSignals] = useState<DoorThreadSignalsResponse | 'error' | null>(null);
  // The item's history, newest first a page at a time ("Show older" pages
  // back). Refetched after actions via updated_at (a reroute adds events).
  const history = useHistoryPages((q) => getThreadHistory(thread.id, q), thread.id, thread.updated_at);

  // One reading of the brief (briefPlan.ts), shared with the task-ask
  // modal: once a real case file exists its checklist lives on the case
  // (rendered above), so the brief carries only Merlin's own filing. Layout
  // D (Jess's pick, kanban 538d6a0d): a filing whose facts carry tile
  // headlines shows them as situation tiles, the chain folds under them and
  // Do next groups by desk, from the steps as filed.
  const { filing, briefProposal, filedChain, tiles, doNext } = briefPlan(thread, caseFile);
  const keyFacts = filing ? briefKeyFacts(filing, briefProposal) : { visible: [], overflow: [] };
  const hasKeyFactChips = keyFacts.visible.length > 0 || keyFacts.overflow.length > 0;
  // Merlin's cause chain leads the brief when the filing has one; each step
  // carries its fact, so only the facts no step points at stay listed below.
  // Filings from before the chain list every fact, as they always did.
  const chain = tiles.length > 0 ? filedChain : filing ? causeChainSteps(filing, doNext) : [];
  const issue = (filing?.issue ?? '').trim();
  const deskGroups = tiles.length > 0 ? doNextByDesk(stepsForDesks(doNext), viewerName) : [];
  // A case item that restates one of the Do next steps borrows that step's
  // desk for its Ask button (QT-10054's "Clear SHERW01 CreditHold Y…" →
  // step 9, Laura Caswell).
  const taskDesks = taskDesksFor(caseFile?.tasks ?? [], doNext);
  const keyFactRows =
    !filing || tiles.length > 0
      ? []
      : chain.length > 0
        ? factsOffTheChain(filing, chain)
        : keyFactItems(filing);
  const detailParts = filing ? briefDetailParts(filing, briefProposal) : [];
  const splitParent = splitParentNo(thread);
  const children = splitChildren(threads, thread);
  const live = thread.status === 'routed' || thread.status === 'open';
  // Sent on to someone else: not the viewer's call any more.
  const withSomeoneElse = live && !!viewerName && !isRoutedToUser(thread, viewerName);
  const closed = thread.status === 'completed' || thread.status === 'dismissed';
  // The account's sales rep a customer desk's recommendation names (kanban
  // d3915b86); fetched only while a live thread has a customer desk.
  const customerRelay = useCustomerRelay(
    !closed && deskGroups.some((g) => g.customer) ? { kind: 'thread', id: thread.id } : null,
  );
  // A case that is completed or cancelled closes the item too: its header
  // must not keep a live triage badge such as "Awaiting evidence" (fan-out
  // finding 13), even while the thread row itself still says in_investigation.
  const caseClosed = caseFile?.status === 'completed' || caseFile?.status === 'cancelled';
  const showLiveBadges = !closed && !caseClosed;
  // Do next shows its desks' Ask buttons, which send its step drafts; the
  // asks list leaves out exactly those (one place to send each draft).
  const doNextShown = !!filing && !closed && deskGroups.length > 0;
  const deskDraftIds = doNextShown ? deskOwnedDraftIds(deskGroups, thread, asks) : new Set<string>();
  // The issue leads the pane, right under the title (Scott, 2026-10-06:
  // "pull it further up to the top"; proposal 3 had it above the decision
  // buttons only). Its tiles come with it unless the item is a case: the
  // case file is the record then, and the brief folds under it with the
  // tiles. No issue filed: nothing moves.
  const issueOnTop = !!issue;
  const tilesOnTop = issueOnTop && !caseFile;
  // "Why it came to you" prints the routing reason; the reopened box then
  // leaves it out rather than say it twice.
  const whyShown = live && !!whyLine(thread);

  useEffect(() => {
    let cancelled = false;
    setSignals(null);
    listThreadSignals(thread.id)
      .then((data) => {
        if (!cancelled) setSignals(data);
      })
      .catch(() => {
        if (!cancelled) setSignals('error');
      });
    return () => {
      cancelled = true;
    };
  }, [thread.id]);

  // A different thread means different role targets — drop the cached set.
  useEffect(() => {
    setRouteTargets(null);
    setSelectedRoles(new Set());
  }, [thread.id]);

  // Load the picker (roles + staff) the first time the reroute form — or the
  // where-it-stands form, whose "waiting on" picks from the same staff —
  // opens.
  useEffect(() => {
    // The hand-off form uses the same picker, so it loads it too.
    if ((actionMode !== 'reroute' && actionMode !== 'posture' && actionMode !== 'handoff') || routeTargets !== null) return;
    let cancelled = false;
    listThreadRouteTargets(thread.id)
      .then((targets) => {
        if (!cancelled) setRouteTargets(targets);
      })
      .catch(() => {
        if (!cancelled) setRouteTargets({ roles: [], staff: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [actionMode, routeTargets, thread.id]);

  // "Send it" sat below the fold under the people list; once the form has
  // its people, bring its buttons into view.
  const rerouteBtnsRef = useRef<HTMLDivElement | null>(null);
  const routeInputRef = useRef<HTMLInputElement | null>(null);
  const routeNoteRef = useRef<HTMLDivElement | null>(null);
  const peoplePicksRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if ((actionMode !== 'reroute' && actionMode !== 'handoff') || routeTargets === null) return;
    // Instant, not smooth: a smooth scroll moved the people list under the
    // reader's click after the screen had settled (turn 2).
    rerouteBtnsRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
  }, [actionMode, routeTargets]);

  /**
   * Run one change. A failure names the action (`Could not close QT-… as
   * handled.`), so a second control that also fails never looks identical
   * to the first (fan-out finding 3).
   */
  const runAction = async (what: string, action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(failedActionMessage(what, e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Open one of the inline steps. Starting another action clears the last
   * error (it described something else), and the buttons only ever OPEN
   * their step: pressing "Send to someone else" a second time used to close
   * the form, and reopening it brought back the old pick and error. The form
   * starts clean whenever it is opened from anywhere else (fan-out finding 8).
   */
  const openMode = (mode: Exclude<ActionMode, 'none' | 'posture'>, prefillRouteTo = '') => {
    setError(null);
    if ((mode === 'reroute' || mode === 'handoff') && (actionMode !== mode || prefillRouteTo)) {
      setRouteTo(prefillRouteTo);
      setSelectedRoles(new Set());
      setRerouteNote('');
    }
    setActionMode(mode);
  };

  const no = thread.thread_no;
  const identity = primaryRef(thread, caseFile);

  const openInvestigation = () => {
    setActionMode('none');
    void runAction(`open a case for ${no}`, async () => {
      const result = await openThreadInvestigation(thread.id);
      onInvestigationOpened(result.thread, result.investigation);
      setJustOpenedInvId(result.investigation.id);
      returnFocusToList();
    });
  };

  const markDone = () => {
    setActionMode('none');
    // No reason required (Scott: "I'll just add a Done button") — this
    // records a LEGITIMATE issue as handled, unlike "Not an issue".
    void runAction(`close ${no} as handled`, async () => {
      onThreadUpdated(await completeThread(thread.id));
      returnFocusToList();
    });
  };

  const pullBack = () => {
    // The step stays open, saying "Pulling it back…", until the server
    // answers: it takes seconds, and closing it first left the header
    // saying "With Roblyn" as if nothing happened (UX round 2026-10-06).
    void runAction(`pull ${no} back to your queue`, async () => {
      if (!viewerName) return;
      // The same reroute the Routed away tab's Pull back uses, aimed at the
      // viewer, so the routing playbook learns it and the audit records who.
      const result = await rerouteThread(thread.id, viewerName, 'Pulled back to my queue');
      trackEvent('action', 'qcp_pull_back', { thread_no: thread.thread_no, from: 'item', outcome: 'ok' });
      onThreadUpdated(result.thread);
      setRouteReceipt(null);
      setActionMode('none');
    });
  };

  const sendReopen = () =>
    void runAction(`reopen ${no}`, async () => {
      onThreadUpdated(await reopenThread(thread.id, reopenNote.trim() || undefined));
      setActionMode('none');
      setReopenNote('');
    });

  /**
   * Who the picker says, as the backend should receive it, or null (with the
   * error set) when it cannot go yet. A typed name must be one person in the
   * staff list before it is sent: a loose name was sent as typed, matched
   * nobody and emailed no one (case round, finding 2). The same holds for
   * the case hand-off, which delivers through the same path. Without a
   * staff list there is nothing to check against, and the name goes as
   * typed, as before. Role chips come first (the backend resolves them from
   * the account), then whatever was typed — one comma-joined list.
   */
  const checkedTarget = (): string | null => {
    let typed = routeTo;
    if (staff.length > 0 && routeTo.trim()) {
      const names = resolvedNames(nameChecks);
      if (!names) {
        setError('Pick who you mean for each name flagged above before sending.');
        return null;
      }
      typed = names.join(', ');
    }
    const target = composeRouteTo(routeTargets?.roles ?? [], selectedRoles, typed);
    if (!target) {
      setError('Pick a role or type the name of who this should go to.');
      return null;
    }
    return target;
  };

  const sendReroute = () => {
    const target = checkedTarget();
    if (!target) return;
    const note = rerouteNote.trim();
    if (standingRule && !note) {
      setError('Say why in the note — the reason is the rule Merlin will apply.');
      return;
    }
    void runAction(`send ${no} to someone else`, async () => {
      const result = standingRule
        ? await rerouteThread(thread.id, target, note, { standingRule: true })
        : await rerouteThread(thread.id, target, note || undefined);
      onThreadUpdated(result.thread);
      setRouteReceipt(result.delivery);
      setActionMode('none');
      setRouteTo('');
      setSelectedRoles(new Set());
      setRerouteNote('');
      setStandingRule(false);
      // Focus goes back to the list, never to the stage button the page
      // shows next (proposal 9).
      returnFocusToList();
    });
  };

  /**
   * "This is not a quality case" (owner ruling, review bz2bb6: "Jess can do
   * that"). Withdraws the case and routes the item on in one step, through
   * the same picker and the same delivery as an ordinary reroute.
   */
  const sendHandOff = () => {
    const target = checkedTarget();
    if (!target) return;
    const reason = rerouteNote.trim();
    if (!reason) {
      setError('Say why it is not a quality case — the person you hand it to reads it.');
      return;
    }
    if (!caseFile) return;
    void runAction(`hand ${caseFile.investigation_no} to ${target}`, async () => {
      const result = await handOffInvestigation(caseFile.id, target, reason);
      onThreadUpdated(result.thread);
      // The case is cancelled by the same call; hand the new row up or the
      // pane keeps showing the stage it was in before the hand-off.
      onInvestigationUpdated(result.investigation);
      setRouteReceipt(result.delivery);
      setActionMode('none');
      setRouteTo('');
      setSelectedRoles(new Set());
      setRerouteNote('');
      // Focus goes back to the list, never to the stage button the page
      // shows next (proposal 9).
      returnFocusToList();
    });
  };

  const sendDismiss = () => {
    const reason = dismissReason.trim();
    if (!reason) {
      setError('Say a word about why — it teaches Merlin what to skip.');
      return;
    }
    void runAction(`dismiss ${no} as not an issue`, async () => {
      onThreadUpdated(
        dismissRule
          ? await dismissThread(thread.id, reason, { standingRule: true })
          : await dismissThread(thread.id, reason),
      );
      setActionMode('none');
      setDismissReason('');
      setDismissRule(false);
      returnFocusToList();
    });
  };

  const sendPosture = (toState: ThreadWorkState) => {
    if (toState === thread.work_state) return;
    void runAction(`set where ${no} stands`, async () => {
      onThreadUpdated(
        await setThreadWorkState(thread.id, toState, postureNote.trim() || undefined),
      );
      setActionMode('none');
      setPostureNote('');
    });
  };

  const sendWaitingOn = () => {
    const who = waitWho.trim();
    if (!who) {
      setError('Say who it is waiting on: pick a colleague or type a name.');
      return;
    }
    // A wait is a record on the item now (card a095e349); a name picked
    // from the directory carries its email so the server pins the person.
    // When several people in the directory have that name, one of them must
    // be picked — never a silent first match (the Send check's rule).
    const sameNamed = staff.filter((t) => t.name.trim().toLowerCase() === who.toLowerCase());
    const picked =
      sameNamed.length > 1
        ? sameNamed.find((t) => t.email !== null && t.email === waitPickEmail)
        : sameNamed[0];
    if (sameNamed.length > 1 && !picked) {
      setError(`${sameNamed.length} people are called ${sameNamed[0].name}: pick which one below.`);
      return;
    }
    void runAction(`mark ${no} as waiting on ${who}`, async () => {
      onThreadUpdated(
        await setThreadWait(thread.id, {
          who_name: who,
          who_email: picked?.email ?? null,
          until: waitUntil || null,
          reason: postureNote.trim() || null,
        }),
      );
      setActionMode('none');
      setPostureNote('');
      setWaitWho('');
      setWaitPickEmail(null);
      setWaitUntil('');
    });
  };

  // "They answered: stop waiting" (waiting filter round). A recorded wait
  // the server already ended as answered is acknowledged; one still
  // waiting (the answer was seen on screen only) is cleared with who
  // answered on the record; a legacy note-based wait goes back to active as
  // before.
  const stopWaiting = (who: string) => {
    void runAction(`stop waiting on ${who} for ${no}`, async () => {
      const w = waitingOn(thread);
      if (w?.source === 'record') {
        onThreadUpdated(
          w.state === 'answered'
            ? await acknowledgeThreadWait(thread.id)
            : await clearThreadWait(thread.id, `${who} answered`),
        );
      } else {
        onThreadUpdated(await setThreadWorkState(thread.id, 'active', `${who} answered; no longer waiting`));
      }
      setActionMode('none');
    });
  };

  const cancelAction = () => {
    setActionMode('none');
    setError(null);
  };

  const staff = routeTargets?.staff ?? [];
  const nameChecks: NameCheck[] =
    actionMode === 'reroute' || actionMode === 'handoff' ? checkTypedNames(routeTo, staff) : [];
  // Who the form would send to now, in words (proposal 7): the green line,
  // the Send button's name, or why Send waits.
  const pick = routePick(routeTargets?.roles ?? [], selectedRoles, routeTo, nameChecks, staff);
  const sendBlocked = pick.kind === 'empty' || pick.kind === 'unresolved' ? pick.reason : null;
  const pickedNames = new Set(nameChecks.flatMap((c) => (c.kind === 'ok' ? [c.name] : [])));
  // The names flagged above the chips; a no-match the chips already report is left out.
  const flagged = flaggedChecks(nameChecks, routeTo, staff);
  // Tab from the name box goes to the note, past the people chips (they are
  // reached with ArrowDown); a flagged name keeps its pick buttons next.
  const onRouteInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      const first = peoplePicksRef.current?.querySelector<HTMLButtonElement>('button[data-chip]');
      if (first) {
        e.preventDefault();
        first.focus();
      }
      return;
    }
    if (e.key !== 'Tab' || e.shiftKey) return;
    if (nameChecks.some((c) => c.kind !== 'ok' && c.candidates.length > 0)) return;
    const note = routeNoteRef.current?.querySelector('textarea');
    if (note) {
      e.preventDefault();
      note.focus();
    }
  };
  const loadedWindow = history.window && history.window !== 'error' ? history.window : null;
  const loadedEvents = loadedWindow?.events ?? null;
  // Who it is with, and whether the hand-off email reached each of them.
  const delivery = withSomeoneElse ? deliveryOutcome(thread, loadedWindow) : null;
  // Did someone it is with answer the hand-off email (notes round)?
  const emailReply = withSomeoneElse ? handOffReply(thread) : null;
  const notDelivered = delivery?.undelivered ?? [];
  // The system assigned it to them and emailed nobody (card 75bf026f): the
  // backend's route email audit says so; the timeline has no line to read.
  const unsent = withSomeoneElse && notDelivered.length === 0 && neverEmailed(thread);
  const waiting = showLiveBadges ? waitingOn(thread) : null;
  // Has the person it waits on answered since? Only what the screen can see:
  // an answered ask from them, a reply to the hand-off email, or a step they
  // took on this item (respondedView.ts).
  const waitResponse = waiting ? responseToWait(thread, asks, loadedWindow) : null;
  // Every Merlin draft waiting for a Send: they go with an Ask sent from
  // here, so a draft to the same person never stays "not sent" beside it.
  // (The banner that also listed them at the top was merged into the
  // rail's "Review and send", UX round 2026-10-27.)
  const chatDrafts = splitAsks(asks).drafts;
  // Who sent it: the timeline's routing line, else the backend's record of
  // the route's origin (the reporter's raw-SQL route leaves no routing line
  // in the timeline, so this is how QT-10055 gets a "by").
  const routedBy = sentBy(loadedEvents) ?? assignedByShort(thread);
  // What moved since this viewer last opened the item (card 10a54f95):
  // null on a first look or until the history and the mark are known.
  // Once the item is a case, what people do lands on the case (notes,
  // asks, checklist), so the card reads that too (UX round 2026-11-03).
  const caseItems = caseFile ? caseActivity(caseFile, asks) : [];
  const since = sinceLastLook(lastSeenAt, loadedWindow, asks, thread, caseItems);
  // The newest real activity, said even when nothing is new (UX round
  // 2026-10-13); shown once the open has been recorded (lastSeenAt known,
  // null included: no earlier open).
  const last = lastSeenAt === undefined ? null : lastActivity(loadedWindow, new Date(), caseItems);
  const loadedSignals = signals && signals !== 'error' ? signals : null;
  const newCount = loadedSignals ? newSignalCount(loadedSignals.signals, seenBefore) : 0;
  // The evidence behind a fact or a step (kanban 1af16b1c) quotes a signal
  // when a ref names one of the thread's own.
  const signalRows = loadedSignals?.signals ?? [];
  // "Why ▸" on a Do next step: only when Merlin backed that step, so a
  // filing from before step_evidence renders exactly as it did.
  const whyFor = (step: { key: string; recommendedNos: number[] }) => {
    const why = filing ? stepEvidenceFor(filing, step) : null;
    if (!why) return null;
    return (
      <DoNextWhy
        why={why}
        from={chainStepsDriving(chain, step.key)}
        signals={signalRows}
        onToggle={(expanded) =>
          trackEvent('action', 'qcp_do_next_why', { thread_no: thread.thread_no, step: step.key, expanded })
        }
      />
    );
  };
  const closureNotice =
    thread.status === 'completed' && loadedSignals
      ? externalClosureNoticeForIssues(loadedSignals.issues ?? [])
      : null;

  // The brief's blocks, built once and ordered below: a filing with
  // situation tiles (layout D) reads issue, tiles, do next, recap, gap,
  // reference numbers; every other filing keeps the order it had.
  const recapBlock = filing ? (
    <>
              <div className={styles.merlinGlance}>
                <span className={styles.glanceLabel}>{issue ? 'Where it stands' : 'The problem'}</span>
                <AssistantMarkdown content={filing.at_a_glance} />
              </div>
    </>
  ) : null;
  const situationBlock = filing ? (
    <>
              {/* 2. The situation — tiles (layout D) when the filing has
                  them, with the cause chain folded beneath; else the chain
                  itself (layout C). */}
              {tiles.length > 0 && !tilesOnTop && <SituationTiles tiles={tiles} signals={signalRows} />}
              {tiles.length > 0 && chain.length > 0 && (
                <details className={styles.chainFold} data-testid="chain-fold">
                  <summary>
                    How we got here, step by step ({chain.length})
                  </summary>
                  <CauseChain steps={chain} numbered={false} />
                </details>
              )}
              {tiles.length === 0 && chain.length > 0 && <CauseChain steps={chain} />}
    </>
  ) : null;
  const keyFactsBlock = filing ? (
    <>
              {/* 3. Key facts — severity, owner, court, the identifiers, then
                  the facts Merlin wrote about the situation (newer filings),
                  each opening on what it checked for that fact. */}
              {(hasKeyFactChips || keyFactRows.length > 0) && (
                <div className={styles.keyFacts} aria-label="Key facts">
                  <span className={styles.keyFactsLabel}>
                    {tiles.length > 0 ? 'Reference numbers' : 'Key facts'}
                  </span>
                  {hasKeyFactChips && (
                    <div className={styles.chipRow} data-plain={tiles.length > 0}>
                      {(filingChipsExpanded
                        ? [...keyFacts.visible, ...keyFacts.overflow]
                        : keyFacts.visible
                      )
                        .filter((fact) => tiles.length === 0 || fact.key !== 'severity')
                        .map((fact) => (
                        <span key={fact.key} className={styles.keyChip} data-fact={fact.key}>
                          <strong>{fact.label}</strong> {fact.value}
                        </span>
                      ))}
                      {keyFacts.overflow.length > 0 && (
                        <button
                          type="button"
                          className={styles.moreChipsBtn}
                          onClick={() => setFilingChipsExpanded((prev) => !prev)}
                        >
                          {filingChipsExpanded ? 'fewer' : `+${keyFacts.overflow.length} more`}
                        </button>
                      )}
                    </div>
                  )}
                  {keyFactRows.length > 0 && (
                    <ul className={styles.keyFactList} data-testid="key-fact-list">
                      {keyFactRows.map((row, i) => (
                        <li key={`${i}-${row.fact}`} className={styles.keyFactRow}>
                          {row.basis ? (
                            <details className={styles.keyFactDetails}>
                              <summary>{row.fact}</summary>
                              <div className={styles.keyFactBasis}>
                                <span className={styles.keyFactBasisLabel}>Checked</span>
                                {row.basis}
                                <EvidenceRefs refs={row.evidence ?? []} signals={signalRows} />
                              </div>
                            </details>
                          ) : (
                            <span className={styles.keyFactPlain}>{row.fact}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
    </>
  ) : null;
  const doNextBlock = filing ? (
    <>
              {/* 4. Do next — one numbered list: the proposed case-file items
                  (their own desk badge), then Merlin's recommended action. */}
              {doNext.length > 0 && !closed && (
                <div className={styles.doNextBox}>
                  <div className={styles.doNextHead}>Do next</div>
                  {deskGroups.length > 0 ? (
                    <div className={styles.deskGroups} aria-label="Do next" role="group">
                      {deskGroups.map((group) => (
                        <section
                          key={group.desk || 'none'}
                          className={styles.deskGroup}
                          data-mine={group.mine}
                        >
                          <h4 className={styles.deskName}>
                            {group.mine
                              ? `You (${group.desk})`
                              : group.customer
                                ? 'The customer'
                                : group.desk || 'No desk named'}
                          </h4>
                          {!group.mine && group.customer && !closed && (
                            // The customer's desk is never asked from here
                            // (Jess, 2026-10-21): quality goes through the
                            // account's sales rep. Say so where the Ask was.
                            <div
                              className={styles.deskRecommend}
                              data-testid="desk-recommend"
                              title={CUSTOMER_STEP_HINT}
                            >
                              <span className={styles.deskRecommendTag}>Recommend</span>
                              <span>{defaultCustomerRecommendation(customerRelay)}</span>
                              <span className={styles.deskHint}>
                                Nothing is sent for the customer&rsquo;s steps; the case file carries the
                                wording, and Merlin can reword it from chat.
                              </span>
                            </div>
                          )}
                          {!group.mine && group.desk && (
                            <DeskAsks
                              desk={group.desk}
                              groups={deskAskGroups(group, thread, asks)}
                              drafts={deskDrafts(group, thread, asks)}
                              onAsk={!closed && !group.customer ? () => setAskFor(group) : undefined}
                              onOpen={setStatusFor}
                              onDraftsDiscarded={onAskUpdated}
                              threadNo={thread.thread_no}
                            />
                          )}
                          <ul className={styles.deskSteps}>
                            {group.steps.map(({ step, text }) => (
                              <li
                                key={step.key}
                                className={styles.doNextStep}
                                data-done={step.done}
                              >
                                <span className={styles.doNextText}>
                                  {step.format === 'markdown' ? (
                                    <AssistantMarkdown content={text} />
                                  ) : (
                                    <>
                                      {step.done ? '✓ ' : ''}
                                      {text}
                                    </>
                                  )}
                                </span>
                                {whyFor(step)}
                              </li>
                            ))}
                          </ul>
                        </section>
                      ))}
                    </div>
                  ) : (
                  <>
                  {/* Filings without tiles: open steps first with who owns
                      each; the done and dropped ones fold away (case round,
                      finding 9). */}
                  <ol className={styles.doNextList} aria-label="Do next">
                    {doNext
                      .filter((step) => !step.done)
                      .map((step) => (
                        <li key={step.key} className={styles.doNextStep} data-done={step.done}>
                          {step.owner && (
                            <span className={styles.proposalOwner} data-owner={step.owner}>
                              {TASK_OWNER_LABEL[step.owner]}
                            </span>
                          )}
                          {step.who && <span className={styles.stepWho}>{step.who}</span>}
                          <span className={styles.doNextText}>
                            {step.format === 'markdown' ? (
                              <AssistantMarkdown content={step.text} />
                            ) : (
                              step.text
                            )}
                          </span>
                          {whyFor(step)}
                        </li>
                      ))}
                  </ol>
                  {doNext.some((step) => step.done) && (
                    <details className={styles.doneSteps} data-testid="done-steps">
                      <summary>
                        {doNext.filter((step) => step.done).length} done or dropped
                      </summary>
                      <ul className={styles.doneStepList}>
                        {doNext
                          .filter((step) => step.done)
                          .map((step) => (
                            <li key={step.key}>✓ {step.text}</li>
                          ))}
                      </ul>
                    </details>
                  )}
                  </>
                  )}
                  {briefProposal && (
                    <p className={styles.proposalHint}>
                      📁 Open investigation lands these on the case. Ask Merlin beside this to
                      add, tick or reword an item.
                    </p>
                  )}
                </div>
              )}
    </>
  ) : null;
  const unconfirmedBlock = filing ? (
    <>
              {/* 5. The honest gap. */}
              {filing.could_not_confirm && (
                <div className={styles.unconfirmedBox}>
                  <strong>Still unconfirmed</strong>
                  <AssistantMarkdown content={filing.could_not_confirm} />
                </div>
              )}
    </>
  ) : null;

  return (
    <div className={styles.detail}>
      {/* ── Head ─────────────────────────────────────────────────── */}
      <div className={styles.head}>
        <div className={styles.meta}>
          <span className={styles.threadNo} data-testid="item-identifier">{identity.label}</span>
          {/* Severity, "Possible issue — take a look" and "Awaiting
              evidence" are triage states; on a closed item they contradicted
              "Done" beside them (a Done item still read WATCH, turn 1). */}
          {showLiveBadges && (
            <>
              <span className={styles.severityPill} data-severity={severityEdge(thread.severity)}>
                {THREAD_SEVERITY_LABEL[thread.severity] ?? thread.severity}
              </span>
              <span className={styles.sourcePill} data-tag={thread.source_tag}>
                {SOURCE_TAG_LABEL[thread.source_tag]}
              </span>
              <button
                type="button"
                className={styles.workStatePill}
                data-tone={WORK_STATE_TONE[thread.work_state] ?? 'none'}
                title={thread.work_state_note || 'Set where this thread stands'}
                // The title is Jess's own note: named by the family, not by it.
                data-action="qcp_posture_open"
                data-target-thread-id={no}
                onClick={() => {
                  setError(null);
                  setActionMode(actionMode === 'posture' ? 'none' : 'posture');
                }}
                disabled={busy || readOnly}
              >
                {/* ▲ / ▼ say rising or falling in words (proposal 10). */}
                {postureWords(thread.work_state, workStateLabel(thread.work_state))}
              </button>
              {waiting && (
                <span
                  className={styles.waitingChip}
                  data-overdue={waitingOverdue(waiting) || undefined}
                  data-testid="waiting-on"
                >
                  {waitingOverdue(waiting)
                    ? `⏰ ${waitingOnLabel(waiting)} · Overdue ${daysWord(daysOverdue(waiting))}`
                    : `⏳ ${waitingOnLabel(waiting)}`}
                </span>
              )}
            </>
          )}
          {/* "Sent to <you> · change" on your own item read as odd (turn 4);
              the decision bar already offers Send to someone else. */}
          {thread.routed_to && !closed && withSomeoneElse && (
            <span className={styles.routedTo}>
              {/* Not "Merlin sent this": after you route it yourself the
                  header must not credit Merlin with your decision. And not
                  "Sent" when nobody was emailed (card 75bf026f). */}
              {unsent ? 'Assigned to' : 'Sent to'}{' '}
              {unsent && (
                <span className={styles.notDeliveredName} data-testid="header-never-emailed">
                  (never emailed){' '}
                </span>
              )}
              {delivery ? (
                delivery.people.map((p, i) => (
                  <span key={p.name}>
                    <strong
                      className={p.state === 'failed' ? styles.notDeliveredName : undefined}
                      title={
                        p.state === 'delivered'
                          ? 'The hand-off email reached them'
                          : p.state === 'failed'
                            ? `Not delivered: ${p.reason}`
                            : 'Delivery of the hand-off email is not confirmed here'
                      }
                    >
                      {p.name}
                    </strong>
                    {i < delivery.people.length - 1 && ', '}
                  </span>
                ))
              ) : (
                <strong>{thread.routed_to}</strong>
              )}
              {/* No "change" link here any more: "Send to someone else" in
                  the bar right below opens the same form (kanban 4a0aee2d). */}
            </span>
          )}
          {closed && (
            <span className={styles.routedTo}>
              {/* Keep the routing visible on the record (Scott, review
                  bs63ro: "I also want to see where they get routed to"). */}
              {thread.routed_to && (
                <>
                  Was routed to <strong>{thread.routed_to}</strong>
                  {' · '}
                </>
              )}
              {thread.status === 'completed'
                ? 'Done — real issue, handled'
                : `Dismissed${thread.dismissed_reason ? ` — ${thread.dismissed_reason}` : ''}`}
            </span>
          )}
        </div>
        {/* One number per case (proposal 6): the others, once, here. */}
        <AlsoKnownAsLine identity={identity} />
        <h2 className={styles.title}>{thread.title}</h2>
        {/* The issue in Merlin's words, first (Scott, 2026-10-06), with its
            situation tiles while the item is not yet a case. */}
        {issueOnTop && (
          <IssueSummaryCard
            issue={issue}
            tiles={tilesOnTop ? tiles : []}
            signals={signalRows}
            owner={caseFile || closed ? '' : (filing?.suggested_owner ?? '').trim()}
            onDetails={() => {
              trackEvent('action', 'qcp_issue_summary_details', { thread_no: thread.thread_no });
              document.getElementById(MERLIN_READ_ID)?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
            }}
          />
        )}
        {/* What moved since the last open, next: Jess opens
            the same item many times in a few days and should not have to
            re-read the brief to learn that nothing did (card 10a54f95). */}
        {(since || last) && <SinceLastLookCard since={since} last={last} threadNo={thread.thread_no} />}
        {/* People's notes lead the item (notes round): a line from a phone
            call, under its writer's name, that Merlin never rewrites. Once
            the item is a case, the case file's own note box is the place to
            write; this one stays only to show notes written before. */}
        {(!caseFile || (thread.note_count ?? 0) > 0) && (
          <NoteBox
            recordId={thread.id}
            noun="item"
            load={() => listThreadNotes(thread.id)}
            save={(text) => addThreadNote(thread.id, text)}
          />
        )}
        {emailReply && (
          <div className={styles.respondedBanner} role="status" data-testid="handoff-reply-banner">
            <span>
              ✓ <strong>{emailReply.who}</strong>{' '}
              {emailReply.how === 'about_item' ? 'wrote about this item by email' : 'replied to the hand-off email'}{' '}
              {respondedWhen(emailReply.at)}
              {emailReply.how === 'about_item' && (emailReply.matchedRefs?.length ?? 0) > 0
                ? ` (mentioning ${emailReply.matchedRefs?.join(', ')})`
                : ''}
              {thread.latest_route_reply?.excerpt ? `: “${thread.latest_route_reply.excerpt}”` : '.'}
            </span>
          </div>
        )}
        {waiting && waitResponse && (
          <div className={styles.respondedBanner} role="status" data-testid="responded-banner">
            <span>
              ✓ <strong>{waitResponse.who}</strong> responded {respondedWhen(waitResponse.at)}
              {waitResponse.question
                ? ` · answered “${waitResponse.question}”`
                : waitResponse.via === 'email'
                  ? waitResponse.how === 'about_item'
                    ? ' · wrote about this item by email'
                    : ' · replied to the hand-off email'
                  : ' · on this item'}
              .
            </span>
            {!readOnly && (<button
              type="button"
              className={styles.respondedStop}
              onClick={() => stopWaiting(waitResponse.who)}
              disabled={busy}
            >
              They answered: stop waiting
            </button>)}
          </div>
        )}
        <PendingFollowupsLine followups={followups} />
        {isReopened(thread) && !withSomeoneElse && (
          <FoldLines
            className={styles.reopenedBanner}
            text={`↩ ${reopenedLine(thread, { withReason: !whyShown })}`}
            trackName="qcp_reopened_fold"
            trackMeta={{ thread_no: thread.thread_no }}
            testId="reopened-banner"
          />
        )}
        {closureNotice && (
          // Done here; the sales-site complaint may still be open there.
          // Same wording as the case file (Scott, 2026-09-15).
          <div
            className={styles.externalClosureNotice}
            role="status"
            data-testid="external-closure-notice"
          >
            <strong>{closureNotice.issueNos.join(', ')}</strong>
            {': '}
            {closureNotice.text}
          </div>
        )}
        {hasPostClosureNote(thread) && (
          <div className={styles.noteAfterClose} role="status">
            {postClosureLine(thread)}
            {thread.post_closure_update_at && (
              <span className={styles.noteAfterCloseAge}>
                {' '}· {ageLabel(thread.post_closure_update_at)}
              </span>
            )}
          </div>
        )}
        {/* The note-after-closure box above already quotes the posture note
            — don't print it twice. */}
        {/* A wait's reason ("credit ruling") under the chip that names who
            and until — from the record (card a095e349) or a legacy note. */}
        {waiting?.note && !hasPostClosureNote(thread) && (
          <div className={styles.workStateNote}>{waiting.note}</div>
        )}
        {!waiting &&
          showLiveBadges &&
          thread.work_state !== 'active' &&
          thread.work_state_note &&
          !hasPostClosureNote(thread) &&
          // A brief that leads with the issue says it once: both explorers
          // of the tile layout read this note as a second, older summary
          // that disagreed with the first (kanban 538d6a0d).
          !issue && <div className={styles.workStateNote}>{thread.work_state_note}</div>}
        {/* Why it came here and who sent it, near the top (case round,
            finding 5): most items that reach Jess are not his. */}
        {whyShown && (
          /* One line folded; opened, the RULES Merlin applied in order
             (kanban 143454d3: Jess, 2026-10-06 — "Show less has more…
             than the show all"). The toggle only shows when opening
             reveals something the line does not say. */
          <div className={styles.why} data-testid="why-routed" data-open={whyOpen || undefined}>
            <div className={styles.whyHead}>
              <p
                className={styles.whyLine}
                data-open={whyOpen || undefined}
                title={whyOpen ? undefined : whyLine(thread)}
              >
                <span className={styles.whyLabel}>
                  {withSomeoneElse ? 'Why it went to them' : 'Why it came to you'}
                  {routedBy ? ` · sent ${routedBy === 'by hand' ? 'by hand' : `by ${routedBy}`}` : ''}:
                </span>{' '}
                <span className={styles.whyText}>{whyLine(thread)}</span>
              </p>
              {(hasWhyDetails(thread) || whyLine(thread).length > WHY_FOLD_AT) && (
                <button
                  type="button"
                  className={styles.whyMore}
                  aria-expanded={whyOpen}
                  aria-controls="why-routed-rules"
                  onClick={() => {
                    setWhyOpen(!whyOpen);
                    trackEvent('action', 'qcp_why_routed_fold', { thread_no: thread.thread_no, expanded: !whyOpen });
                  }}
                >
                  {whyOpen ? 'Less ▴' : 'Details ▸'}
                </button>
              )}
            </div>
            {whyOpen && hasWhyDetails(thread) && (
              <ol
                id="why-routed-rules"
                className={styles.whyRules}
                data-testid="why-routed-rules"
                aria-label="The rules behind this route, in order"
              >
                {whyRules(thread).map((rule, i) => (
                  <li
                    key={`${rule.kind}-${rule.instruction_id ?? i}`}
                    data-kind={rule.kind}
                    data-applied={rule.applied || undefined}
                  >
                    <span className={styles.whyRuleState}>{ruleStateLabel(rule)}</span>
                    <span className={styles.whyRuleText}>{rule.text}</span>
                  </li>
                ))}
                {rulesOnFileLine(thread) && (
                  <li className={styles.whyRulesFoot} data-kind="count">
                    {rulesOnFileLine(thread)}
                  </li>
                )}
              </ol>
            )}
          </div>
        )}
        {(splitParent || children.length > 0) && (
          <div className={styles.splitRow} aria-label="Split lineage">
            {splitParent && (
              <button
                type="button"
                className={styles.splitChip}
                onClick={() => {
                  const parent = threadByNo(threads, splitParent);
                  if (parent) onJumpToThread(parent);
                }}
                disabled={!threadByNo(threads, splitParent)}
                title="Merlin split this out of a thread that bundled distinct problems"
                data-action="qcp_split_parent_open"
                data-target-thread-id={splitParent}
              >
                ⑂ Split out of {splitParent}
              </button>
            )}
            {children.length > 0 && (
              <span className={styles.splitGroup}>
                <span className={styles.splitLabel}>⑂ Split into</span>
                {children.map((child) => (
                  <button
                    key={child.id}
                    type="button"
                    className={styles.splitChip}
                    onClick={() => onJumpToThread(child)}
                    title={child.title}
                    data-action="qcp_split_child_open"
                    data-target-thread-id={child.thread_no}
                  >
                    {child.thread_no}
                  </button>
                ))}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Decision bar ─────────────────────────────────────────── */}
      {withSomeoneElse && (
        <div className={styles.actionBar} data-testid="with-someone-else">
          {notDelivered.length > 0 ? (
            /* A send that did not arrive says so (case round, finding 1):
               an undelivered item is not with anyone. */
            <div className={styles.notDelivered} role="alert" data-testid="not-delivered">
              {notDelivered.map((p) => (
                <p key={p.name} className={styles.notDeliveredLine}>
                  ⚠ <strong>Not delivered to {p.name}</strong>: {p.reason}.
                </p>
              ))}
              <p className={styles.notDeliveredSub}>
                {delivery?.nobodyDelivered
                  ? 'Nobody has been told about this item, so it is not with anyone yet.'
                  : [
                      (delivery?.people ?? []).some((p) => p.delivered)
                        ? `Delivered to ${joinNames(
                            (delivery?.people ?? []).filter((p) => p.delivered).map((p) => p.name),
                          )}.`
                        : '',
                      (delivery?.unconfirmed ?? []).length > 0
                        ? `Not confirmed for ${joinNames((delivery?.unconfirmed ?? []).map((p) => p.name))}.`
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
              </p>
              {!readOnly && (<button
                type="button"
                className={styles.actPrimary}
                onClick={() =>
                  openMode('reroute', notDelivered.map((p) => p.name).join(', '))
                }
                disabled={busy}
              >
                Send it again, picking the person
              </button>)}
            </div>
          ) : unsent ? (
            /* Merlin's nightly router wrote the route by raw SQL and no
               hand-off email went out (QT-10055, QT-10016): the people named
               do not know they hold it. The owner did not want a "send it
               now" here (review 2r8pgd) but to know where the route was
               requested from: who assigned it, by which path, when, and
               from whom — read from the audit trail by the backend. */
            <div className={styles.notDelivered} role="alert" data-testid="never-emailed">
              <p className={styles.notDeliveredLine}>
                ⚠ <strong>Never emailed.</strong> {thread.routed_to} do not know they have this
                item.
              </p>
              <p className={styles.notDeliveredLine} data-testid="never-emailed-origin">
                <strong>Where it came from:</strong> {whereFromLine(thread)}
                {splitParent ? ` It was split out of ${splitParent}.` : ''}
              </p>
            </div>
          ) : (
            <span className={styles.withThem}>
              With <strong>{thread.routed_to}</strong> now. It is theirs to decide.
              {/* A send whose outcome is not written yet, history that does
                  not reach the newest rows, or no line about them: say so
                  plainly, never as an alarm. */}
              {(delivery?.unconfirmed ?? []).length > 0 && (
                <span className={styles.deliveryUnconfirmed} data-testid="delivery-unconfirmed">
                  {' '}
                  Delivery of the email to{' '}
                  {joinNames((delivery?.unconfirmed ?? []).map((p) => p.name))} is not confirmed here.
                </span>
              )}
            </span>
          )}
          {!readOnly && (<button
            type="button"
            className={styles.actRoute}
            onClick={() => openMode('confirmPullBack')}
            disabled={busy}
          >
            ↩ Pull back to me
          </button>)}
          {!readOnly && (<button
            type="button"
            className={styles.actGhost}
            onClick={() => openMode('reroute')}
            disabled={busy}
            data-shortcut="s"
            data-shortcut-name="send_to_someone_else"
          >
            Send to someone else
          </button>)}
        </div>
      )}
      {live && !withSomeoneElse && !readOnly && (
        /* One compact row (Scott, 2026-10-06). What each decision does —
           who sees it, whose it is afterwards, where it goes (case round,
           finding 10) — is the button's tooltip, and opens in full under
           "What each does ▸" for touch and keyboard readers. */
        <>
          <div className={`${styles.actionBar} ${styles.decisionBar}`} data-testid="decision-bar">
            <button
              type="button"
              className={styles.actPrimary}
              onClick={() => openMode('confirmOpen')}
              disabled={busy}
              data-shortcut="o"
              data-shortcut-name="open_investigation"
              title={DECISION_HINTS.open}
              data-action="qcp_decision_open_case"
              data-target-thread-id={no}
            >
              ✔ Open investigation
            </button>
            <button
              type="button"
              className={styles.actDone}
              onClick={() => openMode('confirmDone')}
              disabled={busy}
              data-shortcut="d"
              data-shortcut-name="done"
              title={DECISION_HINTS.done}
              data-action="qcp_decision_done"
              data-target-thread-id={no}
            >
              ✓ Done — issue handled
            </button>
            <button
              type="button"
              className={styles.actRoute}
              onClick={() => openMode('reroute')}
              disabled={busy}
              data-shortcut="s"
              data-shortcut-name="send_to_someone_else"
              title={DECISION_HINTS.route}
              data-action="qcp_decision_route"
              data-target-thread-id={no}
            >
              ↪ Send to someone else
            </button>
            <button
              type="button"
              className={styles.actGhost}
              onClick={() => openMode('dismiss')}
              disabled={busy}
              data-shortcut="n"
              data-shortcut-name="not_an_issue"
              title={DECISION_HINTS.dismiss}
              data-action="qcp_decision_not_issue"
              data-target-thread-id={no}
            >
              Not an issue
            </button>
            <button
              type="button"
              className={styles.decisionHintsBtn}
              aria-expanded={hintsOpen}
              aria-controls="decision-hints"
              onClick={() => {
                setHintsOpen(!hintsOpen);
                trackEvent('action', 'qcp_decision_hints_fold', { thread_no: thread.thread_no, expanded: !hintsOpen });
              }}
            >
              {hintsOpen ? 'Hide ▴' : 'What each does ▸'}
            </button>
          </div>
          {hintsOpen && (
            <ul id="decision-hints" className={styles.decisionHints} data-testid="decision-hints">
              <li>
                <strong>Open investigation</strong>
                <span>{DECISION_HINTS.open}</span>
              </li>
              <li>
                <strong>Done — issue handled</strong>
                <span>{DECISION_HINTS.done}</span>
              </li>
              <li>
                <strong>Send to someone else</strong>
                <span>{DECISION_HINTS.route}</span>
              </li>
              <li>
                <strong>Not an issue</strong>
                <span>{DECISION_HINTS.dismiss}</span>
              </li>
            </ul>
          )}
        </>
      )}
      {/* A thread that became a case has no decision bar: three explorer runs
          looked for a way off a case that was not a quality problem and found
          none (owner ruling bz2bb6: "Jess can do that"). */}
      {!live && !closed && caseFile && !readOnly && (
        <div className={styles.actionBar} data-testid="case-handoff-bar">
          <button
            type="button"
            className={styles.actRoute}
            style={{ marginLeft: 'auto' }}
            onClick={() => openMode('handoff')}
            disabled={busy}
            title="Not a quality problem after all — close the case and send the item to the right team"
            data-action="qcp_decision_handoff"
            data-target-thread-id={no}
          >
            ↪ Not a quality case — hand it on
          </button>
        </div>
      )}

      {closed && !readOnly && (
        <div className={styles.actionBar}>
          <button
            type="button"
            className={styles.actReopen}
            onClick={() => openMode('reopen')}
            disabled={busy}
            title="Put this back in your queue — it came back, or was closed too soon"
            data-action="qcp_decision_reopen"
            data-target-thread-id={no}
          >
            ↩ Reopen
          </button>
          {/* Scott (2026-09-08): "take one that's done and put it back in
              the cases" — same promotion as a routed thread, recorded as a
              reopen straight into the case file. */}
          <button
            type="button"
            className={styles.actPrimary}
            onClick={() => openMode('confirmOpen')}
            disabled={busy}
            title="Put this back in Cases — reopens it straight into an INV case file, no queue detour"
            data-action="qcp_decision_open_case"
            data-target-thread-id={no}
          >
            ✔ Open a case
          </button>
          <span className={styles.actHint}>
            Reopen sends it back to {thread.routed_to || 'your queue'}; a case skips the queue
          </span>
        </div>
      )}

      {actionMode === 'confirmOpen' && (
        <ConfirmStep
          label="Confirm opening a case"
          question={`Open a case for ${no}?`}
          detail={
            closed
              ? 'It goes straight back into Cases as a case file.'
              : 'It moves to the Cases tab, where you track it to closed.'
          }
          yes="Open the case"
          yesClass={styles.actPrimary}
          no="Not now"
          busy={busy}
          onYes={openInvestigation}
          onNo={cancelAction}
          track={{ yes: 'qcp_thread_open_case', threadNo: no }}
        />
      )}
      {actionMode === 'confirmDone' && (
        <ConfirmStep
          label="Confirm closing as handled"
          question={`Close ${no} as a real issue, handled?`}
          detail="It moves to Done. You can reopen it from there."
          yes="Close it as handled"
          yesClass={styles.actDone}
          no="Not now"
          busy={busy}
          onYes={markDone}
          onNo={cancelAction}
          track={{ yes: 'qcp_thread_close_done', threadNo: no }}
        />
      )}
      {actionMode === 'confirmPullBack' && (
        <ConfirmStep
          label="Confirm pulling it back"
          question={`Pull ${no} back to your queue?`}
          detail={`It will no longer be with ${thread.routed_to ?? 'them'}.`}
          yes={busy ? 'Pulling it back…' : 'Pull it back'}
          yesClass={styles.actRoute}
          no="Leave it with them"
          busy={busy}
          onYes={pullBack}
          onNo={cancelAction}
          track={{ yes: 'qcp_thread_pull_back', threadNo: no }}
        />
      )}

      {actionMode === 'reopen' && (
        <div className={styles.inlineAction}>
          <AudioNotesInput
            value={reopenNote}
            onChange={setReopenNote}
            disabled={busy}
            label="Why is it back? (optional — type it or record a voice note)"
            placeholder="e.g. same lot short-filled again on AU01902…"
            rows={2}
          />
          <div className={styles.inlineActionBtns}>
            <button
              type="button"
              className={styles.actReopen}
              onClick={sendReopen}
              disabled={busy}
              data-action="qcp_thread_reopen"
              data-target-thread-id={no}
            >
              Reopen thread
            </button>
            <button
              type="button"
              className={styles.actGhost}
              onClick={cancelAction}
              disabled={busy}
              data-action="qcp_decision_cancel"
              data-target-thread-id={no}
            >
              Leave it closed
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      {gated.length > 0 && actionMode === 'none' && (
        <div className={styles.inlineAction} aria-label="Hand-off check" data-testid="handoff-gate">
          {gated.map((h) => (
            <p key={h.sweep_item_id} className={h.state === 'held' ? styles.receiptWarn : styles.receiptOk}>
              {h.state === 'held' ? '✋ ' : '⏳ '}
              {gatedHandoffLine(h)}{' '}
              {!readOnly && (<button
                type="button"
                className={styles.actRoute}
                onClick={() => void sendAnyway(h.to_email)}
                disabled={busy}
                data-action="qcp_handoff_send_anyway"
                data-thread-id={thread.id}
                data-sweep-item-id={h.sweep_item_id}
                data-handoff-state={h.state}
              >
                {h.state === 'held' ? 'Send anyway' : 'Send now'}
              </button>)}
            </p>
          ))}
        </div>
      )}

      {routeReceipt && actionMode === 'none' && (
        <div className={styles.inlineAction} aria-label="Route delivery receipt">
          {(routeReceipt.pending ?? []).map((p) => (
            <p key={`pending-${p.email}`} className={styles.receiptOk} data-testid="route-pending">
              ⏳ Not emailed yet: Merlin is checking whether {p.name} already knows this. The
              email goes out once that is settled, or you can send it now above.
            </p>
          ))}
          {routeReceipt.emailed.map((e) => (
            <p key={e.email} className={styles.receiptOk}>
              ✉ Emailed {e.name} ({e.email})
              {e.role_label ? ` as the ${e.role_label.charAt(0).toLowerCase()}${e.role_label.slice(1)}` : ''}
            </p>
          ))}
          {routeReceipt.unresolved.map((u) => (
            <p key={u.segment} className={styles.receiptWarn}>
              {u.role && u.reason ? (
                <>⚠ {u.reason} Nobody was emailed for that role.</>
              ) : (
                <>
                  ⚠ No staff match for “{u.segment}” — nobody was emailed.
                  {u.candidates.length > 0 && ` Did you mean ${u.candidates.join(', ')}?`} The
                  reroute is recorded; try again with a full name.
                </>
              )}
            </p>
          ))}
          {routeReceipt.failed.map((f) => (
            <p key={`${f.name}-${f.email ?? ''}`} className={styles.receiptWarn}>
              ⚠ Email to {f.name} failed — they were NOT notified ({f.error}).
            </p>
          ))}
          {(routeReceipt.covered_by ?? []).length > 0 && (
            <p className={styles.receiptOk} data-testid="route-covered-by">
              ✓ Routed, but not emailed: they already have this via{' '}
              {(routeReceipt.covered_by ?? []).join('; ')}.
            </p>
          )}
          {(routeReceipt.self_routed ?? []).length > 0 && (
            <p className={styles.receiptOk} data-testid="route-self-routed">
              ✓ Back in your queue. Nobody was emailed.
            </p>
          )}
        </div>
      )}

      {(actionMode === 'reroute' || actionMode === 'handoff') && (
        <div className={styles.inlineAction}>
          <div className={styles.inlineActionHead}>
            {actionMode === 'handoff'
              ? `Hand ${caseFile?.investigation_no ?? 'this case'} to another team`
              : 'Send this to someone else'}
            <span className={styles.inlineActionSub}>
              {actionMode === 'handoff'
                ? 'The case closes with your reason on it, and the item goes to them'
                : 'Merlin remembers the correction for next time'}
            </span>
          </div>
          {/* The account's people first — resolved from Merlin's data for
              this thread's customer, highlighted with who they actually are
              (Jess, QT-10036). Greyed out, with the reason, when the account
              has nobody on file; the backend refuses those anyway. */}
          {routeTargets === null ? (
            <span className={styles.roleChipsLoading}>Looking up this account’s people…</span>
          ) : (
            <RouteRoleChoices
              roles={routeTargets.roles}
              selected={selectedRoles}
              onChange={setSelectedRoles}
              disabled={busy}
              threadNo={thread.thread_no}
            />
          )}
          {/* The name box waits for the people list: shown at once, it slid
              down when the list arrived and the typing landed nowhere. */}
          {routeTargets !== null && (
            <>
              <span className={styles.orTypeLabel}>
                {selectedRoles.size > 0 ? 'Also send to (optional):' : 'Or type a name:'}
              </span>
              <input
                ref={routeInputRef}
                className={styles.textInput}
                value={routeTo}
                onChange={(e) => setRouteTo(e.target.value)}
                onKeyDown={onRouteInputKey}
                placeholder="Who should this go to? Type a name or pick one below"
                autoFocus
                aria-label="Route to"
                disabled={busy}
                aria-invalid={nameChecks.some((c) => c.kind !== 'ok') || undefined}
              />
              {/* Each typed name must be one person before it is sent (case
                  round, finding 2): flagged here, resolved by a pick. */}
              {flagged.length > 0 && (
                <div className={styles.nameChecks} role="alert" data-testid="name-check">
                  {flagged.map((c) => (
                    <div key={c.typed} className={styles.nameCheck}>
                      <span>
                        {c.kind === 'several'
                          ? `“${c.typed}” could be ${c.candidates.length} people. Pick one:`
                          : c.kind === 'not_a_name'
                            ? 'That reads as a sentence, not a name. Put notes in the note box and pick the person:'
                            : c.candidates.length > 0
                              ? `Nobody in the staff list is called “${c.typed}”. Did you mean:`
                              : `Nobody in the staff list is called “${c.typed}”. Clear the box to see everyone.`}
                      </span>
                      {c.candidates.map((name) => (
                        <button
                          key={name}
                          type="button"
                          className={styles.staffPick}
                          disabled={busy}
                          onClick={() => setRouteTo(pickFor(routeTo, c.typed, name))}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {/* A visible pick-list instead of a native <datalist>, filtered
                  on what is typed (proposal 7). */}
              <RoutePeoplePicks
                staff={staff}
                typed={routeTo}
                picked={pickedNames}
                onPick={setRouteTo}
                onClear={() => {
                  setRouteTo('');
                  routeInputRef.current?.focus();
                }}
                onLeaveUp={() => routeInputRef.current?.focus()}
                disabled={busy}
                threadNo={thread.thread_no}
                groupRef={peoplePicksRef}
              />
            </>
          )}
          <div ref={routeNoteRef}>
          <AudioNotesInput
            value={rerouteNote}
            onChange={setRerouteNote}
            disabled={busy}
            label={
              actionMode === 'handoff'
                ? 'Why is it not a quality case? (they read this, and it goes on the record)'
                : standingRule
                  ? 'Not mine because… (they read this, and it becomes the rule Merlin applies to items like this)'
                  : 'Note for them, and why it is theirs (type it or record a voice note, optional)'
            }
            placeholder="e.g. 'packaging issues go to production'"
            rows={2}
          />
          </div>
          {actionMode === 'reroute' && (
            /* "Not mine because…" as a RULE (kanban 143454d3): recorded
               where Merlin's nightly router reads its instructions, and on
               the admin Quality Routing page for Scott. */
            <label className={styles.ruleToggle} data-testid="standing-rule-toggle">
              <input
                type="checkbox"
                checked={standingRule}
                disabled={busy}
                onChange={(e) => {
                  setStandingRule(e.target.checked);
                  trackEvent('action', 'qcp_standing_rule_toggle', {
                    thread_no: thread.thread_no,
                    from: 'reroute',
                    on: e.target.checked,
                  });
                }}
              />
              <span>
                <strong>Make this a rule:</strong> items like this go to them. Merlin applies it on the
                next one and says so; Scott can see and retire it.
              </span>
            </label>
          )}
          {/* Under the note, beside Send (UX round 2026-10-20): above it, the
              line appeared on a pick and pushed the note box down, so the
              click meant for the box missed it and the typing went to the
              page. */}
          {routeTargets !== null && <RouteConfirmLine pick={pick} threadNo={thread.thread_no} />}
          <div className={styles.inlineActionBtns} ref={rerouteBtnsRef}>
            <button
              type="button"
              className={styles.actPrimary}
              onClick={actionMode === 'handoff' ? sendHandOff : sendReroute}
              disabled={busy || sendBlocked !== null}
              data-action={actionMode === 'handoff' ? 'qcp_handoff_send' : 'qcp_route_send'}
              data-target-thread-id={no}
              aria-describedby={sendBlocked ? 'route-send-reason' : undefined}
            >
              {actionMode === 'handoff' ? (busy ? 'Handing it on…' : 'Hand it on') : sendLabel(pick, busy)}
            </button>
            <button
              type="button"
              className={styles.actGhost}
              onClick={cancelAction}
              disabled={busy}
              data-action="qcp_decision_cancel"
              data-target-thread-id={no}
            >
              Cancel
            </button>
            {busy && (
              <span className={pickerStyles.sending} role="status" data-testid="route-sending">
                <span className={pickerStyles.spinner} aria-hidden="true" /> {ROUTE_SENDING_LINE}
              </span>
            )}
            {sendBlocked && (
              <span id="route-send-reason" className={pickerStyles.sendReason} data-testid="route-send-reason">
                {sendBlocked}
              </span>
            )}
          </div>
        </div>
      )}

      {actionMode === 'dismiss' && (
        <div className={styles.inlineAction}>
          <AudioNotesInput
            value={dismissReason}
            onChange={setDismissReason}
            disabled={busy}
            label="Why skip this one? (type it or record a voice note)"
            placeholder="e.g. homeowner misuse, already handled, not a product problem…"
            rows={2}
          />
          <label className={styles.ruleToggle} data-testid="dismiss-rule-toggle">
            <input
              type="checkbox"
              checked={dismissRule}
              disabled={busy}
              onChange={(e) => {
                setDismissRule(e.target.checked);
                trackEvent('action', 'qcp_standing_rule_toggle', {
                  thread_no: thread.thread_no,
                  from: 'dismiss',
                  on: e.target.checked,
                });
              }}
            />
            <span>
              <strong>Don’t send me items like this:</strong> the reason becomes a rule Merlin
              applies, and Scott can see and retire it.
            </span>
          </label>
          <div className={styles.inlineActionBtns}>
            <button
              type="button"
              className={styles.actDanger}
              onClick={sendDismiss}
              disabled={busy}
              data-action="qcp_thread_dismiss"
              data-target-thread-id={no}
            >
              Dismiss thread
            </button>
            <button
              type="button"
              className={styles.actGhost}
              onClick={cancelAction}
              disabled={busy}
              data-action="qcp_decision_cancel"
              data-target-thread-id={no}
            >
              Keep it
            </button>
          </div>
        </div>
      )}

      {actionMode === 'posture' && (
        <div className={styles.inlineAction}>
          <div className={styles.inlineActionHead}>
            Where does this thread stand?
            <span className={styles.inlineActionSub}>
              Merlin keeps this current too — this sets it by hand
            </span>
          </div>
          <input
            className={styles.textInput}
            value={postureNote}
            onChange={(e) => setPostureNote(e.target.value)}
            placeholder="Why? e.g. 'contractor back next week with the container' (optional)"
            aria-label="Posture note"
            disabled={busy}
          />
          {/* Waiting on someone (case round, finding 4): a colleague picked
              from the directory, or free text, and until when. */}
          <div className={styles.waitRow} role="group" aria-label="Waiting on someone else">
            <span className={styles.orTypeLabel}>Or it is waiting on someone else:</span>
            <input
              className={styles.textInput}
              value={waitWho}
              onChange={(e) => {
                setWaitWho(e.target.value);
                setWaitPickEmail(null);
              }}
              placeholder="Who? Pick below or type, e.g. the lab"
              aria-label="Waiting on who"
              disabled={busy}
            />
            <label className={styles.waitUntil}>
              Until
              <input
                type="date"
                className={styles.textInput}
                value={waitUntil}
                onChange={(e) => setWaitUntil(e.target.value)}
                aria-label="Until (optional)"
                disabled={busy}
              />
            </label>
            <button type="button" className={styles.actRoute} onClick={sendWaitingOn} disabled={busy}>
              Mark as waiting on them
            </button>
          </div>
          {waitWho.trim().length > 0 && staff.length > 0 && (
            <div className={styles.staffPicks} role="group" aria-label="People it could be waiting on">
              {staff
                .filter((t) => t.name.toLowerCase().includes(waitWho.trim().toLowerCase()))
                .slice(0, 12)
                .map((t, i, shown) => {
                  // A name two people share shows their emails, so the pick
                  // says which one (and the record keeps that one's id).
                  const shared = shown.filter((o) => o.name === t.name).length > 1;
                  const on = waitWho === t.name && (!shared || waitPickEmail === t.email);
                  return (
                    <button
                      key={`${t.name}-${t.email ?? i}`}
                      type="button"
                      className={`${styles.staffPick} ${on ? styles.staffPickOn : ''}`}
                      aria-pressed={on}
                      disabled={busy}
                      data-action="qcp_wait_person_pick"
                      onClick={() => {
                        setWaitWho(t.name);
                        setWaitPickEmail(t.email);
                      }}
                    >
                      {shared && t.email ? `${t.name} (${t.email})` : t.name}
                    </button>
                  );
                })}
            </div>
          )}
          <div className={styles.inlineActionBtns}>
            {(Object.keys(WORK_STATE_LABEL) as ThreadWorkState[]).map((state) => (
              <button
                key={state}
                type="button"
                className={state === thread.work_state ? styles.actPrimary : styles.actGhost}
                onClick={() => sendPosture(state)}
                disabled={busy || state === thread.work_state}
              >
                {postureWords(state, WORK_STATE_LABEL[state])}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── The case file — one canonical view ────────────────────── */}
      {caseFile && (
        <CaseFileSection
          investigation={caseFile}
          onUpdated={onInvestigationUpdated}
          onPopOut={popOutCase}
          note={
            justOpenedInvId === caseFile.id
              ? `✔ ${caseFile.investigation_no} opened from this thread`
              : undefined
          }
          embedded
          openAskCount={asks.filter((r) => r.status === 'open').length}
          activeConversationId={thread.conversation_id}
          asks={asks}
          onAskTask={onAskTask}
          taskDesks={taskDesks}
        />
      )}

      {/* ── Asks that belong here ─────────────────────────────────── */}
      <CaseAsksHost
        title={caseFile ? 'Asks on this case' : 'Asks on this thread'}
        requests={asks}
        onRequestUpdated={onAskUpdated}
        // Do next's Ask buttons own their desks' drafts while Do next is on
        // screen; any other draft (a closed item's, one on the reader's own
        // desk) is listed here with its Send.
        hideDraftIds={deskDraftIds}
        trackContext={{ thread_no: thread.thread_no, investigation_no: caseFile?.investigation_no }}
      />

      {/* ── Merlin's read ─────────────────────────────────────────── */}
      <section className={styles.read} aria-label="Merlin's read" id={MERLIN_READ_ID}>
        <FoldOnceCaseExists folded={!!caseFile}>
        {filing ? (
          <div className={styles.merlinTurn}>
            <span className={styles.merlinAvatar}>{MERLIN_ICON}</span>
            <div className={styles.merlinBody}>
              <div className={styles.merlinByline}>
                <strong>Merlin</strong>
                {thread.investigated_at && (
                  <span>looked into this · {investigatedAtLabel(thread.investigated_at)}</span>
                )}
              </div>
              {/* 1. The problem — the issue line itself leads the pane, up
                  by the title; the brief starts at the situation. Same
                  renderer as the chat: Merlin writes dense findings and
                  reaches for tables and lists. */}
              {tiles.length > 0 ? (
                <>
                  {situationBlock}
                  {doNextBlock}
                  {recapBlock}
                  {unconfirmedBlock}
                  {keyFactsBlock}
                </>
              ) : (
                <>
                  {recapBlock}
                  {situationBlock}
                  {keyFactsBlock}
                  {doNextBlock}
                  {unconfirmedBlock}
                </>
              )}
            </div>
          </div>
        ) : (
          <p className={styles.noFiling}>
            Merlin routed this but hasn’t filed an investigation yet — the summary is what it
            saw: {thread.summary || '(no summary)'}
          </p>
        )}
        </FoldOnceCaseExists>
      </section>

      {/* ── Evidence ──────────────────────────────────────────────── */}
      <section className={styles.evidence} aria-label="Evidence">
        <div className={styles.evidenceHead}>
          {signalCountLabel(thread.signal_ids.length) || 'No signals'} behind it
          {newCount > 0 && (
            <span className={styles.newCount} title="Landed since you last read this thread">
              {newCount} NEW
            </span>
          )}
        </div>
        {loadedWindow && (loadedWindow.events.length > 0 || loadedWindow.has_more) && (
          // Newest first; people's actions and the delivery lines in full,
          // Merlin's housekeeping folded into one line per run (case round,
          // finding 8).
          <div className={styles.timeline} aria-label="Thread timeline">
            <div className={styles.evidenceDivider}>Timeline · newest first</div>
            {quietTimeline(loadedWindow.events).map((entry) =>
              entry.kind === 'event' ? (
                <div key={entry.key} className={styles.timelineRow} data-event={entry.event.event_type}>
                  <span className={styles.timelineDot} data-actor={entry.event.actor_kind} />
                  <span className={styles.timelineDate}>{signalDateLabel(entry.event.created_at)}</span>
                  <span className={styles.timelineText}>
                    <span className={styles.timelineWho}>
                      {entry.event.actor_label || actorWord(entry.event.actor_kind)}
                    </span>
                    {entry.event.summary}
                  </span>
                </div>
              ) : (
                <div key={entry.key} className={styles.timelineFold}>
                  <button
                    type="button"
                    className={styles.moreChipsBtn}
                    aria-expanded={!!openFolds[entry.key]}
                    onClick={() => setOpenFolds((prev) => ({ ...prev, [entry.key]: !prev[entry.key] }))}
                  >
                    {foldedLabel(entry.events)} · {openFolds[entry.key] ? 'hide' : 'show'}
                  </button>
                  {openFolds[entry.key] &&
                    entry.events.map((ev, i) => (
                      <div key={`${entry.key}-${i}`} className={styles.timelineRow} data-event={ev.event_type}>
                        <span className={styles.timelineDot} data-actor={ev.actor_kind} />
                        <span className={styles.timelineDate}>{signalDateLabel(ev.created_at)}</span>
                        <span className={styles.timelineText}>{ev.summary}</span>
                      </div>
                    ))}
                </div>
              ),
            )}
            {loadedWindow?.has_more && (
              <button
                type="button"
                className={styles.moreChipsBtn}
                onClick={history.showOlder}
                disabled={history.loadingOlder}
              >
                {history.loadingOlder ? 'Loading older timeline…' : 'Show older'}
              </button>
            )}
            {history.olderError && (
              <div className={styles.error} role="alert">
                {history.olderError}
              </div>
            )}
          </div>
        )}
        {/* The trace and the rest, folded away down here by the timeline
            (Jess, 2026-10-01: the key points up top, the provenance out of
            the way but still there to open). */}
        {filing && detailParts.length > 0 && (
          <details className={styles.briefDetails} data-testid="brief-details">
            <summary>Details · {detailParts.join(' · ')}</summary>
            <div className={styles.briefDetailsBody}>
              {filing.traced.trim() && (
                <div className={styles.tracedBox}>
                  <div className={styles.tracedHead}>How Merlin traced it</div>
                  <div className={styles.tracedText}>
                    <AssistantMarkdown content={filing.traced} />
                  </div>
                </div>
              )}
              {briefProposal?.current_state.trim() && (
                <div className={styles.proposalState}>
                  <strong>Where it stands</strong>
                  <AssistantMarkdown content={briefProposal.current_state} />
                </div>
              )}
              {briefProposal && briefProposal.findings_notes.length > 0 && (
                <div className={styles.proposalState}>
                  <strong>Merlin’s notes</strong>
                  <ul className={styles.proposalNotes} aria-label="Merlin's notes">
                    {briefProposal.findings_notes.map((note, i) => (
                      <li key={`${i}-${note}`}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
              {briefProposal && briefProposal.answered_questions.length > 0 && (
                <div className={styles.proposalAnswered}>
                  <strong>Already answered</strong>
                  <dl>
                    {briefProposal.answered_questions.map((qa, i) => (
                      <div key={`${i}-${qa.question}`}>
                        <dt>{qa.question}</dt>
                        <dd>{qa.answer}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {filing.checked.length > 0 && (
                <div className={styles.checkedRow}>
                  <span className={styles.checkedLabel}>Checked</span>
                  {filing.checked.map((source) => (
                    <span key={source} className={styles.checkedPill}>
                      {source}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </details>
        )}
        {/* Every key the thread hangs on, folded to one line (kanban
            1af16b1c — Jess: the "+189 more" chip row under QT-10026 was
            white noise). The brief's Reference numbers row above keeps the
            few that matter; this is the full set, for when one is needed. */}
        {evidenceBridgeKeys(thread).length > 0 && (
          <details className={styles.briefDetails} data-testid="all-keys">
            <summary>All reference keys · {evidenceBridgeKeys(thread).length}</summary>
            <div className={styles.chipRow}>
              {(() => {
                const { visible, overflow } = organizeChips(evidenceBridgeKeys(thread));
                return [...visible, ...overflow].map(([k, v]) => (
                  <span key={k} className={styles.keyChip}>
                    <strong>{keyLabel(k)}</strong> {v}
                  </span>
                ));
              })()}
            </div>
          </details>
        )}
        {signals === 'error' && (
          <div className={styles.evidenceCard}>
            <span className={styles.evidenceHint}>
              The signals bank is unreachable right now — ask Merlin beside this to walk
              through the evidence.
            </span>
          </div>
        )}
        {signals === null && thread.signal_ids.length > 0 && (
          <div className={styles.evidenceCard}>
            <span className={styles.evidenceHint}>Loading the signals…</span>
          </div>
        )}
        {loadedSignals && (
          <>
            {groupSignalsByDay(loadedSignals.signals, signalDateLabel).map((group) => {
              // Repeated escalation emails land as near-identical signals —
              // a day header + collapse says "Aug 19 · 4 updates" instead
              // of printing the same paragraph four times.
              const collapsed = group.signals.length > 2 && !expandedDays[group.label];
              const shown = collapsed ? group.signals.slice(0, 1) : group.signals;
              return (
                <div key={group.label} className={styles.evidenceDay}>
                  {group.signals.length > 1 && (
                    <div className={styles.evidenceDayHead}>
                      {group.label} · {group.signals.length} updates
                    </div>
                  )}
                  {shown.map((sig) => {
                    const fresh = isNewSignal(sig, seenBefore);
                    return (
                      <div
                        key={sig.id}
                        className={`${styles.evidenceCard} ${fresh ? styles.evidenceCardNew : ''}`}
                        data-new={fresh || undefined}
                      >
                        <span className={styles.evidenceMeta}>
                          {fresh && <span className={styles.newMark}>NEW</span>}
                          <span className={styles.evidenceDate}>
                            {signalDateLabel(sig.created_at)}
                          </span>
                          {sig.source && <span>{sig.source}</span>}
                        </span>
                        <span className={styles.evidenceText}>{sig.observation}</span>
                        {sig.significance && (
                          <span className={styles.evidenceHint}>{sig.significance}</span>
                        )}
                      </div>
                    );
                  })}
                  {group.signals.length > 2 && (
                    <button
                      type="button"
                      className={styles.moreChipsBtn}
                      onClick={() =>
                        setExpandedDays((prev) => ({ ...prev, [group.label]: collapsed }))
                      }
                    >
                      {collapsed
                        ? `Show ${group.signals.length - 1} more from ${group.label}`
                        : 'fewer'}
                    </button>
                  )}
                </div>
              );
            })}
            {/* Once, in words, instead of one id card per missing signal
                (case round, finding 12). */}
            {loadedSignals.missing.length > 0 && (
              <div className={styles.evidenceCard} data-testid="signals-gone">
                <span className={styles.evidenceHint}>
                  {loadedSignals.signals.length === 0
                    ? 'The original emails and notes behind this item are no longer available.'
                    : loadedSignals.missing.length === 1
                      ? '1 more signal cannot be shown: its original email or note is no longer available.'
                      : `${loadedSignals.missing.length} more signals cannot be shown: their original email or note is no longer available.`}
                </span>
              </div>
            )}
          </>
        )}
        {thread.quality_issue_ids.length > 0 && (
          <>
            <div className={styles.evidenceDivider}>Formal issues</div>
            {!loadedSignals || !loadedSignals.issues
              ? // Until the lazy fetch lands (or if it failed) the bare ids
                // keep the evidence count honest.
                thread.quality_issue_ids.map((id) => (
                  <div key={id} className={`${styles.evidenceCard} ${styles.evidenceIssue}`}>
                    <span className={styles.evidenceId}>{id}</span>
                  </div>
                ))
              : (
                <>
                  {loadedSignals.issues.map((issue) => (
                    <div
                      key={issue.id}
                      className={`${styles.evidenceCard} ${styles.evidenceIssue}`}
                    >
                      <span className={styles.evidenceMeta}>
                        <span className={styles.evidenceDate}>{issue.issue_no}</span>
                        <span>{issue.status.replace(/_/g, ' ')}</span>
                      </span>
                      <span className={styles.evidenceText}>{issue.title}</span>
                    </div>
                  ))}
                  {(loadedSignals.missing_issues ?? []).map((id) => (
                    <div key={id} className={`${styles.evidenceCard} ${styles.evidenceIssue}`}>
                      <span className={styles.evidenceId}>{id}</span>
                      <span className={styles.evidenceHint}>Not found in the complaints log.</span>
                    </div>
                  ))}
                </>
              )}
          </>
        )}
      </section>

      {/* Popped out over the workspace — the same modal everywhere. */}
      {casePopped && caseFile && (
        <CaseFileModal
          investigation={caseFile}
          onClose={closeCasePopout}
          onUpdated={onInvestigationUpdated}
          asks={asks}
          onAskTask={onAskTask}
          taskDesks={taskDesks}
        />
      )}
      {askFor && (
        <AskModal
          key={askFor.desk}
          scope={{ kind: 'thread', id: thread.id, no: thread.thread_no, label: identity.label }}
          desk={askFor.desk}
          stepNo={askFor.steps.find((st) => !st.step.done)?.no ?? askFor.steps[0]?.no ?? null}
          steps={askFor.steps.filter((st) => !st.step.done).map((st) => st.text)}
          drafts={deskDrafts(askFor, thread, asks)}
          chatDrafts={chatDrafts}
          viewerName={viewerName}
          onClose={() => setAskFor(null)}
          onSent={(result) => {
            [...result.sent, ...result.discarded].forEach(onAskUpdated);
            onAskSent?.();
            setAskFor(null);
          }}
          onDiscarded={(discarded) => {
            discarded.forEach(onAskUpdated);
            setAskFor(null);
          }}
        />
      )}
      {statusFor && (
        <AskStatusCard
          threadNo={thread.thread_no}
          group={deskAskGroups({ desk: '', steps: [] }, thread, asks).find((g) => g.id === statusFor.id) ?? statusFor}
          onClose={() => setStatusFor(null)}
          onRequestUpdated={onAskUpdated}
          onOpenThread={() => {
            setStatusFor(null);
            document.getElementById('case-asks')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      )}
    </div>
  );
}

export default ThreadDetailPane;
